import moment from 'moment';

/**
 * Who was on a company's programme at the end of a given month.
 *
 * ⚠️ THE COMPANY REPORT'S TWO CARDS COUNT DIFFERENT DAYS, ON PURPOSE.
 *
 *   Billing          the roster as the month OPENED. The client's rule is that
 *                    joiners during a billable month land on the next invoice,
 *                    so the opening head count is what gets charged.
 *   Summary & Goals  everyone on the programme BY MONTH END, because they were
 *                    there and they trained.
 *
 * Both are right. Presented side by side with no explanation they read as a
 * contradiction — "1 billable" next to "5 / 5 have a device connected" — which
 * is exactly what was reported. The screens now state the difference instead of
 * hiding it, and `joinersDuringMonth` below is the number they state.
 *
 * Separately, Summary & Goals used to filter on nothing at all: a report for
 * July counted people who joined in October, and a past month's figures changed
 * every time somebody new signed up. That was a plain bug.
 */

/**
 * @param userList  rows from /admin/company/user/list/:id
 * @param ownEmail  the company's own management login, which is not a participant
 * @param monthKey  `YYYY-MM`
 */
export const rosterAtMonthEnd = (userList, ownEmail, monthKey) => {
    const list = Array.isArray(userList) ? userList : [];
    /*
     * ⚠️ UTC, TO MATCH THE BILLING ENGINE.
     *
     * helpers/corporateBilling.js builds every month boundary from Date.UTC on
     * purpose, so month lengths and DST cannot move them. A local-time cutoff
     * here disagreed with it by the viewer's offset: in IST a member who joined
     * at 23:59Z on the 31st fell into the NEXT month for this report while the
     * invoice counted them in this one, so the joiner note would be off by one
     * around every month boundary. Caught by the boundary test.
     */
    const cutoff = moment.utc(`${monthKey}-01`, 'YYYY-MM-DD').endOf('month');
    return list
        .filter((u) => !ownEmail || u?.email !== ownEmail)
        // A member with no join date is KEPT. An undated legacy row is far more
        // likely to predate the month than to postdate it, and dropping it would
        // silently shrink the report with no way for a reader to notice.
        .filter((u) => !u?.createdAt || moment.utc(u.createdAt).isSameOrBefore(cutoff));
};

/**
 * How many were on the roster by month end but not in the opening head count
 * the invoice was built from.
 *
 * Compared against the ACCEPTED count rather than the billable figure, so it is
 * like for like whichever population the plan happens to bill — under the
 * `active` mode the billable number is a subset and the difference would
 * otherwise be nonsense.
 */
export const joinersDuringMonth = (rosterCount, openingAccepted) => {
    const roster = Number(rosterCount) || 0;
    if (openingAccepted === undefined || openingAccepted === null) return 0;
    return Math.max(0, roster - Number(openingAccepted));
};

export default rosterAtMonthEnd;
